/**
 * ConsoleVariableManager - console variable (CVar) system with type-safe access and callbacks.
 */

import { Delegate } from '../utils/Delegate.js';

import { ObjectTracker } from './ObjectTracker.js';



export class ConsoleVariable {
  private value: number;
  public onValueChanged = new Delegate<[number /*old value*/, number /*new value*/]>();

  constructor(
    public readonly name: string,
    public readonly defaultValue: number,
    public readonly description?: string,
    public readonly min?: number,
    public readonly max?: number
  ) {
    ObjectTracker.track(this);
    this.value = defaultValue;
  }

  get(): number {
    return this.value;
  }

  set(newValue: number): number {
    if (this.min !== undefined) {
      newValue = Math.max(newValue, this.min);
    }
    if (this.max !== undefined) {
      newValue = Math.min(newValue, this.max);
    }

    const oldValue = this.value;
    this.value = newValue;
    if (newValue !== oldValue) {
      this.onValueChanged.invoke(oldValue, newValue);
    }
    return newValue;
  }

  reset(): void {
    this.value = this.defaultValue;
  }

  hasConstraints(): boolean {
    return this.min !== undefined || this.max !== undefined;
  }

  getConstraintString(): string {
    if (!this.hasConstraints()) {
      return '';
    }
    return `[${this.min ?? '-∞'}, ${this.max ?? '∞'}]`;
  }

  getFullDescription(): string {
    let desc = `${this.name} = ${this.value} (default: ${this.defaultValue})`;

    if (this.hasConstraints()) {
      desc += ` ${this.getConstraintString()}`;
    }

    if (this.description) {
      desc += ` - ${this.description}`;
    }

    return desc;
  }
}

export class ConsoleVariableManager {
  private static instance: ConsoleVariableManager | null = null;
  private variables = new Map<string, ConsoleVariable>();
  private changeCallbacks = new Map<string, ((newValue: number, oldValue: number) => void)[]>();

  private constructor() {
    ObjectTracker.track(this);
  }

  /** @returns The singleton instance */
  static getInstance(): ConsoleVariableManager {
    ConsoleVariableManager.instance ??= new ConsoleVariableManager();
    return ConsoleVariableManager.instance;
  }

  /**
   * Register a new console variable.
   * @param name - Variable name (case-insensitive)
   * @param default - Default numerical value
   * @param description - Human-readable description
   * @param min - Optional minimum value
   * @param max - Optional maximum value
   */
  registerVariable(data: {
    name: string,
    default: number,
    description?: string,
    min?: number,
    max?: number
  }): ConsoleVariable {
    const normalizedName = data.name.toLowerCase();

    if (this.variables.has(normalizedName)) {
      console.warn(`Console variable '${data.name}' is already registered`);
      return this.variables.get(normalizedName)!;
    }

    const variable = new ConsoleVariable(normalizedName, data.default, data.description, data.min, data.max);
    this.variables.set(normalizedName, variable);
    return variable;
  }

  /**
   * Get the current value of a console variable.
   * @param fallback - Value returned if variable doesn't exist
   */
  getValue(name: string, fallback: number = 0): number {
    const variable = this.variables.get(name.toLowerCase());
    return variable ? variable.get() : fallback;
  }

  /**
   * Set the value of a console variable.
   * @returns True if variable was set successfully
   */
  setValue(name: string, value: number): boolean {
    const variable = this.variables.get(name.toLowerCase());
    if (!variable) {
      return false;
    }

    const oldValue = variable.get();
    const newValue = variable.set(value);

    // Trigger change callbacks
    const callbacks = this.changeCallbacks.get(variable.name);
    if (callbacks) {
      callbacks.forEach(callback => callback(newValue, oldValue));
    }

    return true;
  }

  /**
   * Reset a variable to its default value.
   * @returns True if variable was reset successfully
   */
  resetVariable(name: string): boolean {
    const variable = this.variables.get(name.toLowerCase());
    if (!variable) {
      return false;
    }

    const oldValue = variable.get();
    variable.reset();

    // Trigger change callbacks
    const callbacks = this.changeCallbacks.get(variable.name);
    if (callbacks) {
      callbacks.forEach(callback => callback(variable.get(), oldValue));
    }

    return true;
  }

  /** @returns Variable or null if not found */
  getVariable(name: string): ConsoleVariable | null {
    return this.variables.get(name.toLowerCase()) ?? null;
  }

  /** @returns Array of all console variables */
  getAllVariables(): ConsoleVariable[] {
    return Array.from(this.variables.values());
  }

  /** @returns True if variable exists */
  hasVariable(name: string): boolean {
    return this.variables.has(name.toLowerCase());
  }

  /**
   * Add a callback that will be called when a variable changes.
   * @param callback - Function to call when variable changes
   */
  onVariableChanged(name: string, callback: (newValue: number, oldValue: number) => void): void {
    const normalizedName = name.toLowerCase();
    if (!this.changeCallbacks.has(normalizedName)) {
      this.changeCallbacks.set(normalizedName, []);
    }
    this.changeCallbacks.get(normalizedName)!.push(callback);
  }

  /**
   * Remove a change callback for a variable.
   * @param callback - Callback function to remove
   */
  removeVariableCallback(name: string, callback: (newValue: number, oldValue: number) => void): void {
    const callbacks = this.changeCallbacks.get(name.toLowerCase());
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Execute a console variable command (used by console system)
   * @param name Variable name
   * @param args Command arguments
   * @returns Response message
   */
  executeVariableCommand(name: string, args: string[]): string {
    const variable = this.variables.get(name.toLowerCase());
    if (!variable) {
      return `Unknown console variable: ${name}`;
    }

    if (args.length === 0) {
      // Show current value using the class method
      return variable.getFullDescription();
    }

    const newValue = parseFloat(args[0]);
    if (isNaN(newValue)) {
      return `Invalid value for ${variable.name}: ${args[0]}`;
    }

    const success = this.setValue(name, newValue);

    if (success) {
      const actualValue = this.getValue(name);
      if (actualValue !== newValue) {
        return `${variable.name} set to ${actualValue} (clamped from ${newValue})`;
      }
      return `${variable.name} set to ${actualValue}`;
    } else {
      return `Failed to set ${variable.name}`;
    }
  }
}

// Export singleton instance for convenience
export const CVarManager = ConsoleVariableManager.getInstance();
